import { useState } from "react";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackConversion } from "@/lib/tracking-events";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { WHATSAPP_URL } from "@/components/site/WhatsAppButton";


const MAX_FILE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB


const PROPERTY_TYPES = [
  "Herança",
  "Apartamento",
  "Moradia",
  "Prédio",
  "Loja",
  "Terreno",
  "Lote",
  "Outro",
] as const;

const CONDITIONS = [
  "Pronto a habitar",
  "Necessita de obras ligeiras",
  "Necessita de obras profundas",
  "Devoluto / Degradado",
] as const;

const schema = z.object({
  name: z.string().trim().min(2, "Indique o seu nome").max(100),
  phone: z.string().trim().min(9, "Telefone inválido").max(20),
  email: z.string().trim().email("Email inválido").max(255),
  property_type: z.enum([...PROPERTY_TYPES], { message: "Selecione o tipo de imóvel" }),
  location: z.string().trim().min(2, "Indique a localização").max(120),
  property_condition: z.enum([...CONDITIONS]).optional(),
  message: z.string().trim().max(1000).optional(),
  accept: z.literal(true, { message: "Aceite os termos" }),
});

export function LeadForm({ compact = false }: { compact?: boolean }) {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [sent, setSent] = useState(false);

  function onFilesChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    const tooBig = picked.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      toast.error(`O ficheiro "${tooBig.name}" excede o limite de 2 GB.`);
      e.target.value = "";
      setFiles([]);
      return;
    }
    setFiles(picked);
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const parsed = schema.safeParse({
      name: fd.get("name"),
      phone: fd.get("phone"),
      email: fd.get("email"),
      property_type: fd.get("property_type"),
      location: fd.get("location"),
      property_condition: (fd.get("property_condition") as string) || undefined,
      message: fd.get("message") ?? "",
      accept: fd.get("accept") === "on",
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);

    const attachments: { path: string; name: string; size: number }[] = [];
    if (files.length) {
      setUploading(true);
      // Unguessable folder (uuid) — required by the storage upload policy.
      const folder =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}`.padEnd(8, "0");
      for (const file of files) {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(-120);
        const path = `${folder}/${safeName}`;

        const { error } = await supabase.storage.from("lead-files").upload(path, file, {
          upsert: false,
          contentType: file.type || "application/octet-stream",
        });
        if (error) {
          setUploading(false);
          setLoading(false);
          toast.error(`Não foi possível carregar "${file.name}".`);
          return;
        }
        attachments.push({ path, name: file.name, size: file.size });
      }
      setUploading(false);
    }

    const res = await fetch("/api/public/lead", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        name: parsed.data.name,
        phone: parsed.data.phone,
        email: parsed.data.email,
        property_type: parsed.data.property_type,
        location: parsed.data.location,
        property_condition: parsed.data.property_condition ?? null,
        message: parsed.data.message || null,
        attachments,
        source: compact ? "hero" : typeof window !== "undefined" ? window.location.pathname : "site",
      }),
    }).catch(() => null);
    setLoading(false);
    if (!res || !res.ok) {
      toast.error("Não foi possível enviar. Tente novamente ou contacte-nos por telefone.");
      return;
    }

    trackConversion(
      "Lead",
      {
        content_name: "Pedido de proposta",
        property_type: parsed.data.property_type,
        location: parsed.data.location,
        form: compact ? "hero" : "pagina",
      },
      { email: parsed.data.email, phone: parsed.data.phone },
    );

    form.reset();
    setFiles([]);
    setSent(true);
  }

  if (sent) {
    return (
      <div role="status" className="rounded-2xl border-2 border-primary bg-primary-soft/40 p-6 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
        <p className="mt-3 font-display text-xl font-semibold text-foreground">Obrigado! Recebemos o seu pedido.</p>
        <p className="mt-2 text-muted-foreground">Apresentamos proposta em 48 h úteis. Pode também falar connosco no WhatsApp.</p>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackConversion("Contact", { method: "whatsapp" })}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-semibold text-white hover:opacity-90"
        >
          <MessageCircle className="h-5 w-5" /> Falar no WhatsApp
        </a>
        <div>
          <button type="button" onClick={() => setSent(false)} className="mt-4 text-sm text-muted-foreground underline hover:text-primary">
            Enviar outro pedido
          </button>
        </div>
      </div>
    );
  }


  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="name">Nome</Label>
          <Input id="name" name="name" required maxLength={100} placeholder="O seu nome" />
        </div>
        <div>
          <Label htmlFor="phone">Telefone</Label>
          <Input id="phone" name="phone" required maxLength={20} placeholder="+351 …" />
        </div>
      </div>
      <div>
        <Label htmlFor="email">E-mail</Label>
        <Input id="email" name="email" type="email" required maxLength={255} placeholder="voce@exemplo.pt" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="property_type">Tipo de imóvel</Label>
          <select
            id="property_type"
            name="property_type"
            required
            defaultValue=""
            className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="" disabled>Selecione…</option>
            {PROPERTY_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="location">Localização (concelho)</Label>
          <Input id="location" name="location" required maxLength={120} placeholder="Ex.: Lisboa, Porto…" />
        </div>
      </div>

      {!compact && (
        <div>
          <Label htmlFor="property_condition">Estado do imóvel</Label>
          <select
            id="property_condition"
            name="property_condition"
            defaultValue=""
            className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Indicar (opcional)…</option>
            {CONDITIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      )}

      {!compact && (
        <div>
          <Label htmlFor="message">Mensagem</Label>
          <Textarea id="message" name="message" rows={4} maxLength={1000} placeholder="Conte-nos brevemente sobre o imóvel (opcional)" />
        </div>
      )}
      <div>
        <Label htmlFor="files">Anexar ficheiros (opcional)</Label>
        <Input
          id="files"
          name="files"
          type="file"
          multiple
          onChange={onFilesChange}
          className="mt-1 cursor-pointer file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Fotografias, caderneta predial, plantas ou documentos. Máximo 2 GB por ficheiro.
        </p>
        {files.length > 0 && (
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {files.map((f) => (
              <li key={f.name}>
                {f.name} — {(f.size / (1024 * 1024)).toFixed(1)} MB
              </li>
            ))}
          </ul>
        )}
      </div>
      <label className="flex items-start gap-2 text-xs text-muted-foreground">
        <input type="checkbox" name="accept" className="mt-0.5 accent-[color:var(--color-primary)]" required />
        <span>
          Li e aceito a{" "}
          <Link to="/politica-de-privacidade" target="_blank" className="underline hover:text-primary">
            Política de Privacidade
          </Link>{" "}
          e o tratamento dos meus dados para fins de contacto e apresentação de proposta.
        </span>
      </label>
      <Button type="submit" disabled={loading} className="w-full sm:w-auto">
        {uploading ? "A carregar ficheiros…" : loading ? "A enviar…" : "Quero vender o meu imóvel"}
      </Button>
    </form>
  );
}
